import { formatPhotoLabelNames } from "@rebuild/shared";
import type { ResponsiveNotePhotoLabel } from "@rebuild/shared/models";
import type { PhotoGalleryItem } from "./photos.types";

/** Mirrors the server's normalizeLabels so the optimistic patch matches what comes back. */
export function mergePhotoLabels(
  existing: ResponsiveNotePhotoLabel[] | undefined,
  incoming: ResponsiveNotePhotoLabel[],
): ResponsiveNotePhotoLabel[] {
  const seenNames = new Set<string>();
  const merged: ResponsiveNotePhotoLabel[] = [];

  for (const label of [...(existing ?? []), ...incoming]) {
    const id = label?.id?.trim();
    const name = label?.name?.trim();
    if (!id || !name) continue;

    const nameKey = name.toLowerCase();
    if (seenNames.has(nameKey)) continue;

    seenNames.add(nameKey);
    merged.push({ id, name });
  }

  return merged;
}

/**
 * Mirrors the server's `remove` branch. Matches on name as well as id because labels dedupe by name:
 * the same tag can sit on one photo under an org preset's uuid and on another under its `freeform:`
 * id, and removing the chip the user actually saw has to work in both cases.
 */
export function removePhotoLabels(
  existing: ResponsiveNotePhotoLabel[] | undefined,
  removed: ResponsiveNotePhotoLabel[],
): ResponsiveNotePhotoLabel[] {
  const removedIds = new Set(removed.map((label) => label.id));
  const removedNames = new Set(removed.map((label) => label.name.trim().toLowerCase()));

  return mergePhotoLabels([], existing ?? []).filter(
    (label) => !removedIds.has(label.id) && !removedNames.has(label.name.toLowerCase()),
  );
}

export function getPhotoLabels(photo: PhotoGalleryItem): ResponsiveNotePhotoLabel[] {
  return photo.responsiveNotePhoto?.labels ?? [];
}

/** Labels can only be persisted on responsive-note photos; legacy estimate files have no per-file write path. */
export function isLabelablePhoto(photo: PhotoGalleryItem): boolean {
  return photo.source === "responsive-note" && Boolean(photo.responsiveNotePhoto?.noteId);
}

export function formatPhotoLabelList(labels: ResponsiveNotePhotoLabel[]): string {
  return formatPhotoLabelNames(labels);
}
